import React from "react";

// Animated Santa Icon - Premium SVG with breathing animation
export const AnimatedSanta = ({ className = "", size = 120 }: { className?: string; size?: number }) => (
  <div className={`relative ${className}`} style={{ width: size, height: size }}>
    <svg viewBox="0 0 120 120" className="w-full h-full">
      <defs>
        <radialGradient id="santaFaceGradient" cx="50%" cy="40%" r="50%">
          <stop offset="0%" stopColor="#FFE4C9" />
          <stop offset="100%" stopColor="#FFDAB9" />
        </radialGradient>
        <radialGradient id="santaHatGradient" cx="30%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#DC2626" />
          <stop offset="100%" stopColor="#991B1B" />
        </radialGradient>
        <filter id="santaGlow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <linearGradient id="beardGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#E5E7EB" />
        </linearGradient>
      </defs>
      
      {/* Glow effect behind */}
      <circle cx="60" cy="60" r="55" fill="url(#santaHatGradient)" opacity="0.15" filter="url(#santaGlow)">
        <animate attributeName="r" values="55;58;55" dur="3s" repeatCount="indefinite" />
      </circle>
      
      {/* Face */}
      <ellipse cx="60" cy="65" rx="30" ry="32" fill="url(#santaFaceGradient)">
        <animate attributeName="ry" values="32;33;32" dur="4s" repeatCount="indefinite" />
      </ellipse>
      
      {/* Beard */}
      <path 
        d="M30 70 Q35 110 60 115 Q85 110 90 70 Q85 95 60 100 Q35 95 30 70" 
        fill="url(#beardGradient)"
        stroke="#D1D5DB"
        strokeWidth="0.5"
      >
        <animate attributeName="d" 
          values="M30 70 Q35 110 60 115 Q85 110 90 70 Q85 95 60 100 Q35 95 30 70;
                  M30 70 Q35 112 60 117 Q85 112 90 70 Q85 97 60 102 Q35 97 30 70;
                  M30 70 Q35 110 60 115 Q85 110 90 70 Q85 95 60 100 Q35 95 30 70" 
          dur="4s" repeatCount="indefinite" />
      </path>
      
      {/* Mustache */}
      <path d="M45 72 Q52 78 60 72 Q68 78 75 72" fill="none" stroke="#FFFFFF" strokeWidth="6" strokeLinecap="round"/>
      <path d="M45 72 Q52 76 60 72 Q68 76 75 72" fill="none" stroke="#E5E7EB" strokeWidth="4" strokeLinecap="round"/>
      
      {/* Eyes */}
      <g className="eyes">
        <ellipse cx="48" cy="55" rx="4" ry="5" fill="#1F2937">
          <animate attributeName="ry" values="5;0.5;5" dur="4s" repeatCount="indefinite" begin="2s"/>
        </ellipse>
        <ellipse cx="72" cy="55" rx="4" ry="5" fill="#1F2937">
          <animate attributeName="ry" values="5;0.5;5" dur="4s" repeatCount="indefinite" begin="2s"/>
        </ellipse>
        {/* Eye sparkle */}
        <circle cx="50" cy="53" r="1.5" fill="white" opacity="0.8"/>
        <circle cx="74" cy="53" r="1.5" fill="white" opacity="0.8"/>
      </g>
      
      {/* Rosy cheeks */}
      <ellipse cx="38" cy="65" rx="6" ry="4" fill="#FDA4AF" opacity="0.6"/>
      <ellipse cx="82" cy="65" rx="6" ry="4" fill="#FDA4AF" opacity="0.6"/>
      
      {/* Nose */}
      <ellipse cx="60" cy="62" rx="5" ry="4" fill="#F87171"/>
      <ellipse cx="58" cy="60" rx="2" ry="1.5" fill="#FCA5A5" opacity="0.5"/>
      
      {/* Hat */}
      <path 
        d="M25 45 Q30 15 60 10 Q90 15 95 45 L90 48 Q60 42 30 48 Z" 
        fill="url(#santaHatGradient)"
      />
      {/* Hat fur trim */}
      <path d="M25 45 Q60 38 95 45 Q95 52 60 50 Q25 52 25 45" fill="white"/>
      
      {/* Hat pompom */}
      <circle cx="60" cy="8" r="8" fill="white">
        <animate attributeName="cy" values="8;6;8" dur="3s" repeatCount="indefinite" />
      </circle>
      
      {/* Eyebrows */}
      <path d="M42 48 Q48 45 54 48" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"/>
      <path d="M66 48 Q72 45 78 48" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  </div>
);

// Animated Elf Icon - Playful elf with waving animation
export const AnimatedElf = ({ className = "", size = 100 }: { className?: string; size?: number }) => (
  <div className={`relative ${className}`} style={{ width: size, height: size }}>
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <defs>
        <radialGradient id="elfFaceGradient" cx="50%" cy="40%" r="50%">
          <stop offset="0%" stopColor="#FFE4C9" />
          <stop offset="100%" stopColor="#FFDAB9" />
        </radialGradient>
        <linearGradient id="elfHatGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#22C55E" />
          <stop offset="100%" stopColor="#15803D" />
        </linearGradient>
        <filter id="elfGlow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Body/Collar */}
      <ellipse cx="50" cy="90" rx="25" ry="15" fill="#22C55E"/>
      <path d="M35 82 L50 95 L65 82" fill="#DC2626"/>
      
      {/* Face */}
      <ellipse cx="50" cy="55" rx="22" ry="24" fill="url(#elfFaceGradient)"/>
      
      {/* Pointy ears */}
      <path d="M25 50 L15 40 L28 48" fill="url(#elfFaceGradient)"/>
      <path d="M75 50 L85 40 L72 48" fill="url(#elfFaceGradient)"/>
      
      {/* Eyes - happy squint */}
      <path d="M40 52 Q43 48 46 52" fill="none" stroke="#1F2937" strokeWidth="2" strokeLinecap="round"/>
      <path d="M54 52 Q57 48 60 52" fill="none" stroke="#1F2937" strokeWidth="2" strokeLinecap="round"/>
      
      {/* Rosy cheeks */}
      <ellipse cx="35" cy="58" rx="5" ry="3" fill="#FDA4AF" opacity="0.6"/>
      <ellipse cx="65" cy="58" rx="5" ry="3" fill="#FDA4AF" opacity="0.6"/>
      
      {/* Big smile */}
      <path d="M40 62 Q50 72 60 62" fill="none" stroke="#1F2937" strokeWidth="2" strokeLinecap="round"/>
      
      {/* Nose */}
      <ellipse cx="50" cy="55" rx="3" ry="2.5" fill="#F87171"/>
      
      {/* Hair */}
      <path d="M30 40 Q35 35 40 38 Q45 32 50 35 Q55 32 60 38 Q65 35 70 40" fill="#92400E"/>
      
      {/* Hat */}
      <path d="M28 42 Q30 25 50 15 Q70 25 72 42 Q50 38 28 42" fill="url(#elfHatGradient)"/>
      <ellipse cx="50" cy="42" rx="24" ry="5" fill="#DC2626"/>
      
      {/* Hat tip with bell */}
      <path d="M50 15 Q75 20 78 30" fill="none" stroke="#22C55E" strokeWidth="8" strokeLinecap="round">
        <animate attributeName="d" 
          values="M50 15 Q75 20 78 30;M50 15 Q78 18 82 28;M50 15 Q75 20 78 30" 
          dur="1s" repeatCount="indefinite" />
      </path>
      
      {/* Bell */}
      <circle cx="80" cy="32" r="6" fill="#EAB308" filter="url(#elfGlow)">
        <animate attributeName="cx" values="80;84;80" dur="1s" repeatCount="indefinite" />
        <animate attributeName="cy" values="32;30;32" dur="1s" repeatCount="indefinite" />
      </circle>
      <ellipse cx="80" cy="36" rx="4" ry="2" fill="#CA8A04">
        <animate attributeName="cx" values="80;84;80" dur="1s" repeatCount="indefinite" />
      </ellipse>
      
      {/* Sparkles around */}
      <g className="sparkles">
        <circle cx="20" cy="25" r="2" fill="#EAB308">
          <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" />
          <animate attributeName="r" values="1;3;1" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="85" cy="55" r="2" fill="#EAB308">
          <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" begin="0.5s"/>
          <animate attributeName="r" values="1;3;1" dur="2s" repeatCount="indefinite" begin="0.5s"/>
        </circle>
        <circle cx="15" cy="70" r="2" fill="#EAB308">
          <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" begin="1s"/>
          <animate attributeName="r" values="1;3;1" dur="2s" repeatCount="indefinite" begin="1s"/>
        </circle>
      </g>
    </svg>
  </div>
);

// Animated Christmas Tree with glowing ornaments
export const AnimatedTree = ({ className = "", size = 100 }: { className?: string; size?: number }) => (
  <div className={`relative ${className}`} style={{ width: size, height: size }}>
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <defs>
        <linearGradient id="treeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#22C55E" />
          <stop offset="50%" stopColor="#16A34A" />
          <stop offset="100%" stopColor="#15803D" />
        </linearGradient>
        <filter id="ornamentGlow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <filter id="starGlow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Tree trunk */}
      <rect x="43" y="88" width="14" height="10" rx="2" fill="#92400E"/>
      
      {/* Tree layers */}
      <polygon points="50,8 75,40 25,40" fill="url(#treeGradient)"/>
      <polygon points="50,25 80,60 20,60" fill="url(#treeGradient)"/>
      <polygon points="50,42 85,88 15,88" fill="url(#treeGradient)"/>
      
      {/* Star on top */}
      <polygon 
        points="50,2 52,10 60,10 54,15 56,23 50,18 44,23 46,15 40,10 48,10" 
        fill="#EAB308"
        filter="url(#starGlow)"
      >
        <animate attributeName="opacity" values="0.8;1;0.8" dur="1.5s" repeatCount="indefinite" />
      </polygon>
      
      {/* Ornaments with glow */}
      <circle cx="40" cy="35" r="4" fill="#DC2626" filter="url(#ornamentGlow)">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="55" cy="32" r="3" fill="#EAB308" filter="url(#ornamentGlow)">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="2s" repeatCount="indefinite" begin="0.3s"/>
      </circle>
      <circle cx="35" cy="55" r="4" fill="#3B82F6" filter="url(#ornamentGlow)">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="2s" repeatCount="indefinite" begin="0.6s"/>
      </circle>
      <circle cx="60" cy="50" r="3.5" fill="#DC2626" filter="url(#ornamentGlow)">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="2s" repeatCount="indefinite" begin="0.9s"/>
      </circle>
      <circle cx="45" cy="70" r="4" fill="#EAB308" filter="url(#ornamentGlow)">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="2s" repeatCount="indefinite" begin="1.2s"/>
      </circle>
      <circle cx="65" cy="72" r="3.5" fill="#3B82F6" filter="url(#ornamentGlow)">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="2s" repeatCount="indefinite" begin="1.5s"/>
      </circle>
      <circle cx="30" cy="78" r="4" fill="#DC2626" filter="url(#ornamentGlow)">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="2s" repeatCount="indefinite" begin="1.8s"/>
      </circle>
      
      {/* Tinsel/garland lines */}
      <path d="M30 42 Q50 48 70 42" fill="none" stroke="#EAB308" strokeWidth="1.5" opacity="0.6"/>
      <path d="M25 62 Q50 70 75 62" fill="none" stroke="#EAB308" strokeWidth="1.5" opacity="0.6"/>
      <path d="M18 82 Q50 92 82 82" fill="none" stroke="#EAB308" strokeWidth="1.5" opacity="0.6"/>
    </svg>
  </div>
);

// Animated Gift Box with ribbon bow
export const AnimatedGift = ({ className = "", size = 80 }: { className?: string; size?: number }) => (
  <div className={`relative ${className}`} style={{ width: size, height: size }}>
    <svg viewBox="0 0 80 80" className="w-full h-full">
      <defs>
        <linearGradient id="giftBoxGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#DC2626" />
          <stop offset="100%" stopColor="#991B1B" />
        </linearGradient>
        <linearGradient id="ribbonGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#EAB308" />
          <stop offset="100%" stopColor="#CA8A04" />
        </linearGradient>
        <filter id="giftGlow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Box shadow/base */}
      <rect x="12" y="35" width="56" height="42" rx="3" fill="#7F1D1D" opacity="0.3"/>
      
      {/* Main box */}
      <rect x="10" y="32" width="60" height="45" rx="4" fill="url(#giftBoxGradient)"/>
      
      {/* Box lid */}
      <rect x="6" y="25" width="68" height="12" rx="3" fill="url(#giftBoxGradient)">
        <animate attributeName="y" values="25;23;25" dur="2s" repeatCount="indefinite" />
      </rect>
      <rect x="6" y="25" width="68" height="4" rx="2" fill="#EF4444" opacity="0.3">
        <animate attributeName="y" values="25;23;25" dur="2s" repeatCount="indefinite" />
      </rect>
      
      {/* Vertical ribbon */}
      <rect x="35" y="25" width="10" height="52" fill="url(#ribbonGradient)">
        <animate attributeName="y" values="25;23;25" dur="2s" repeatCount="indefinite" />
      </rect>
      
      {/* Horizontal ribbon on lid */}
      <rect x="6" y="28" width="68" height="6" fill="url(#ribbonGradient)">
        <animate attributeName="y" values="28;26;28" dur="2s" repeatCount="indefinite" />
      </rect>
      
      {/* Bow loops */}
      <ellipse cx="30" cy="20" rx="12" ry="8" fill="url(#ribbonGradient)" filter="url(#giftGlow)">
        <animate attributeName="cy" values="20;18;20" dur="2s" repeatCount="indefinite" />
        <animate attributeName="ry" values="8;9;8" dur="2s" repeatCount="indefinite" />
      </ellipse>
      <ellipse cx="50" cy="20" rx="12" ry="8" fill="url(#ribbonGradient)" filter="url(#giftGlow)">
        <animate attributeName="cy" values="20;18;20" dur="2s" repeatCount="indefinite" />
        <animate attributeName="ry" values="8;9;8" dur="2s" repeatCount="indefinite" />
      </ellipse>
      
      {/* Bow center knot */}
      <ellipse cx="40" cy="22" rx="6" ry="5" fill="#CA8A04">
        <animate attributeName="cy" values="22;20;22" dur="2s" repeatCount="indefinite" />
      </ellipse>
      
      {/* Bow ribbon tails */}
      <path d="M35 25 Q30 35 25 38" fill="none" stroke="url(#ribbonGradient)" strokeWidth="4" strokeLinecap="round">
        <animate attributeName="d" values="M35 25 Q30 35 25 38;M35 23 Q28 33 23 36;M35 25 Q30 35 25 38" dur="2s" repeatCount="indefinite" />
      </path>
      <path d="M45 25 Q50 35 55 38" fill="none" stroke="url(#ribbonGradient)" strokeWidth="4" strokeLinecap="round">
        <animate attributeName="d" values="M45 25 Q50 35 55 38;M45 23 Q52 33 57 36;M45 25 Q50 35 55 38" dur="2s" repeatCount="indefinite" />
      </path>
      
      {/* Sparkles */}
      <circle cx="15" cy="15" r="2" fill="#EAB308">
        <animate attributeName="opacity" values="0;1;0" dur="1.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="65" cy="12" r="1.5" fill="#EAB308">
        <animate attributeName="opacity" values="0;1;0" dur="1.5s" repeatCount="indefinite" begin="0.5s"/>
      </circle>
      <circle cx="72" cy="45" r="2" fill="#EAB308">
        <animate attributeName="opacity" values="0;1;0" dur="1.5s" repeatCount="indefinite" begin="1s"/>
      </circle>
    </svg>
  </div>
);

// Animated Star with twinkle effect
export const AnimatedStar = ({ className = "", size = 60 }: { className?: string; size?: number }) => (
  <div className={`relative ${className}`} style={{ width: size, height: size }}>
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <defs>
        <linearGradient id="starGoldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FCD34D" />
          <stop offset="50%" stopColor="#EAB308" />
          <stop offset="100%" stopColor="#CA8A04" />
        </linearGradient>
        <filter id="starTwinkle">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Outer glow */}
      <polygon 
        points="30,2 35,22 55,22 39,35 45,55 30,43 15,55 21,35 5,22 25,22" 
        fill="url(#starGoldGradient)"
        opacity="0.3"
        filter="url(#starTwinkle)"
      >
        <animate attributeName="opacity" values="0.2;0.5;0.2" dur="2s" repeatCount="indefinite" />
      </polygon>
      
      {/* Main star */}
      <polygon 
        points="30,5 34,20 50,20 37,30 42,48 30,38 18,48 23,30 10,20 26,20" 
        fill="url(#starGoldGradient)"
        filter="url(#starTwinkle)"
      >
        <animate attributeName="opacity" values="0.9;1;0.9" dur="1.5s" repeatCount="indefinite" />
      </polygon>
      
      {/* Inner highlight */}
      <polygon 
        points="30,12 32,20 40,20 34,25 36,35 30,30 24,35 26,25 20,20 28,20" 
        fill="#FEF3C7"
        opacity="0.6"
      />
      
      {/* Sparkle rays */}
      <line x1="30" y1="0" x2="30" y2="8" stroke="#FCD34D" strokeWidth="2" strokeLinecap="round">
        <animate attributeName="opacity" values="0;1;0" dur="1s" repeatCount="indefinite" />
      </line>
      <line x1="56" y1="20" x2="48" y2="20" stroke="#FCD34D" strokeWidth="2" strokeLinecap="round">
        <animate attributeName="opacity" values="0;1;0" dur="1s" repeatCount="indefinite" begin="0.25s"/>
      </line>
      <line x1="48" y1="48" x2="42" y2="42" stroke="#FCD34D" strokeWidth="2" strokeLinecap="round">
        <animate attributeName="opacity" values="0;1;0" dur="1s" repeatCount="indefinite" begin="0.5s"/>
      </line>
      <line x1="12" y1="48" x2="18" y2="42" stroke="#FCD34D" strokeWidth="2" strokeLinecap="round">
        <animate attributeName="opacity" values="0;1;0" dur="1s" repeatCount="indefinite" begin="0.75s"/>
      </line>
      <line x1="4" y1="20" x2="12" y2="20" stroke="#FCD34D" strokeWidth="2" strokeLinecap="round">
        <animate attributeName="opacity" values="0;1;0" dur="1s" repeatCount="indefinite" begin="1s"/>
      </line>
    </svg>
  </div>
);

// Animated Snowflake
export const AnimatedSnowflake = ({ className = "", size = 50 }: { className?: string; size?: number }) => (
  <div className={`relative ${className}`} style={{ width: size, height: size }}>
    <svg viewBox="0 0 50 50" className="w-full h-full">
      <defs>
        <linearGradient id="snowflakeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#E0F2FE" />
          <stop offset="50%" stopColor="#BAE6FD" />
          <stop offset="100%" stopColor="#7DD3FC" />
        </linearGradient>
        <filter id="snowGlow">
          <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      <g fill="none" stroke="url(#snowflakeGradient)" strokeWidth="2" strokeLinecap="round" filter="url(#snowGlow)">
        <animateTransform 
          attributeName="transform" 
          type="rotate" 
          from="0 25 25" 
          to="360 25 25" 
          dur="20s" 
          repeatCount="indefinite"
        />
        
        {/* Main arms */}
        <line x1="25" y1="5" x2="25" y2="45"/>
        <line x1="5" y1="25" x2="45" y2="25"/>
        <line x1="10" y1="10" x2="40" y2="40"/>
        <line x1="40" y1="10" x2="10" y2="40"/>
        
        {/* Branches on vertical arm */}
        <line x1="25" y1="10" x2="20" y2="15"/>
        <line x1="25" y1="10" x2="30" y2="15"/>
        <line x1="25" y1="40" x2="20" y2="35"/>
        <line x1="25" y1="40" x2="30" y2="35"/>
        
        {/* Branches on horizontal arm */}
        <line x1="10" y1="25" x2="15" y2="20"/>
        <line x1="10" y1="25" x2="15" y2="30"/>
        <line x1="40" y1="25" x2="35" y2="20"/>
        <line x1="40" y1="25" x2="35" y2="30"/>
        
        {/* Center crystal */}
        <circle cx="25" cy="25" r="3" fill="url(#snowflakeGradient)" stroke="none"/>
      </g>
      
      {/* Sparkle effects */}
      <circle cx="25" cy="8" r="1.5" fill="#FFFFFF">
        <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="42" cy="25" r="1.5" fill="#FFFFFF">
        <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" begin="0.5s"/>
      </circle>
      <circle cx="25" cy="42" r="1.5" fill="#FFFFFF">
        <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" begin="1s"/>
      </circle>
      <circle cx="8" cy="25" r="1.5" fill="#FFFFFF">
        <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" begin="1.5s"/>
      </circle>
    </svg>
  </div>
);

// Floating Sparkles Component
export const FloatingSparkles = ({ className = "" }: { className?: string }) => (
  <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
    {[...Array(12)].map((_, i) => (
      <div
        key={i}
        className="absolute"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 3}s`,
        }}
      >
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 16 16" 
          className="animate-sparkle"
          style={{ animationDuration: `${2 + Math.random() * 2}s` }}
        >
          <path 
            d="M8 0L9 6L16 8L9 10L8 16L7 10L0 8L7 6Z" 
            fill="hsl(42 75% 48%)"
            opacity="0.6"
          />
        </svg>
      </div>
    ))}
  </div>
);
